import { useParams } from 'react-router-dom';

import { useMarketPositions } from '@/app/components/VaultsItemPageComponents/hooks/useMarketPositions';
import DataTable, {
  DataTableColumn,
  DataTableDollar,
  DataTableStacked,
  DataTableValue,
} from '@/app/shared/ui/DataTable.tsx';
import { Skeleton } from '@/app/shared/ui/Skeleton';

const desktopGridClassName =
  'min-[960px]:grid min-[960px]:w-full min-[960px]:min-w-0 min-[960px]:grid-cols-[minmax(0,1.2fr)_repeat(6,minmax(0,1fr))] min-[960px]:items-center min-[960px]:gap-2 min-[960px]:px-5 xl:gap-4';

type PositionRow = {
  id: string;
  name: string;
  tranche?: string;
  tokenBalance: string;
  deposited: string;
  currentValue: string;
  apy: string;
  yieldEarned: string;
  terms: string;
};

const POSITIONS_COLUMNS: DataTableColumn<PositionRow>[] = [
  {
    id: 'tranche',
    header: 'Tranche',
    cell: (row) => <DataTableStacked primary={row.name} secondary={row.tranche} />,
  },
  {
    id: 'tokenBalance',
    header: 'Share balance',
    cell: (row) => <DataTableValue value={row.tokenBalance} />,
  },
  {
    id: 'deposited',
    header: 'Deposited',
    cell: (row) => <DataTableDollar value={row.deposited} />,
  },
  {
    id: 'currentValue',
    header: 'Current value',
    cell: (row) => <DataTableDollar value={row.currentValue} />,
  },
  {
    id: 'apy',
    header: 'Current APY',
    cell: (row) => <DataTableValue value={row.apy} />,
  },
  {
    id: 'yieldEarned',
    header: 'Yield earned',
    cell: (row) => <DataTableDollar value={row.yieldEarned} />,
  },
  {
    id: 'terms',
    header: 'Withdrawal terms',
    cell: (row) => <DataTableValue value={row.terms} />,
  },
];

const MarketDetailsPositions = () => {
  const { id } = useParams<{ id: string }>();
  const { positions, updatedAt, isLoading, isConnected } = useMarketPositions(id);

  return (
    <div className='flex min-w-0 flex-col gap-10'>
      <div className='flex flex-col gap-3 min-[960px]:flex-row min-[960px]:items-center min-[960px]:justify-between'>
        <h2 className='text-base font-normal text-brand-1100'>Your Positions</h2>
        <span className='shrink-0 text-base leading-6 text-gray-400'>{updatedAt}</span>
      </div>

      {isLoading ? (
        <div className='flex flex-col gap-3'>
          {Array.from({ length: 2 }).map((_, index) => (
            <Skeleton.Line key={index} loading className='h-[74px] w-full rounded-md'>
              <div className='h-[74px]' />
            </Skeleton.Line>
          ))}
        </div>
      ) : !isConnected ? (
        <p className='text-base text-gray-400'>Connect wallet to see your positions</p>
      ) : (
        <DataTable
          data={positions}
          getRowKey={(row) => row.id}
          desktopGridClassName={desktopGridClassName}
          emptyText='No positions yet'
          pagination={false}
          desktopRowClassName='min-[960px]:h-[74px]'
          columns={POSITIONS_COLUMNS}
        />
      )}
    </div>
  );
};

export default MarketDetailsPositions;
